import { useDisclosure } from "@mantine/hooks";
import { useMemo } from "react";
import FilesModal from "src/components/shared/_Modals/FilesModal/FilesModal";
import { useFilesModal } from "src/components/shared/_Modals/FilesModal/FilesModal.hooks";
import { useCheckPerm } from "src/hooks";
import { type ISummaryItem } from "src/pages/Summary/Summary.types";
import { useSelector } from "src/redux/hooks";
import { Flex, StyledTd, StyledTr } from "src/styled";
import { ENTRY_STATUS } from "src/utils/entryStatus";

import { ReviewText } from "../ReviewsTable.styled";
import ActionsCell from "./Cells/ActionsCell";
import EntryDateOrPatientCell from "./Cells/EntryDateOrPatientCell";
import MedicalStatusCells from "./Cells/MedicalStatusCells";
import OpenedCell from "./Cells/OpenedCell";
import RolesCells from "./Cells/RolesCells";
import SelectCell from "./Cells/SelectCell";
import StatusCell from "./Cells/StatusCell/StatusCell";

interface Props {
  row: ISummaryItem;
  openComments: (item: ISummaryItem) => void;
  isMedicalLog?: boolean;
  isSelect?: boolean;
  rowIndex?: number;
  red?: boolean;
  isPdfExport?: boolean;
}

const SummaryItemRow = ({
  row,
  openComments,
  isMedicalLog,
  isSelect,
  rowIndex,
  red,
  isPdfExport,
}: Props) => {
  const disabled = useMemo(() => row.status === ENTRY_STATUS.WITHDRAWN, [row]);

  const [filesOpened, { open: openFiles, close: closeFiles }] =
    useDisclosure(false);

  const { showRemoved, toggleRemoved, removedNumber, _files } = useFilesModal(
    row.attachments,
  );
  const { showNotesOnSummary, uppercase } = useSelector(
    state => state.preferencesSlice,
  );
  const checkPerm = useCheckPerm();
  return (
    <>
      <StyledTr key={row.lbeid} hover background="transparent">
        <StyledTd red={red}>
          <Flex direction="column">
            {row.colname && (
              <ReviewText fz="13px" disabled={disabled} blue mb={4}>
                {row.colname}
              </ReviewText>
            )}
            <ReviewText fz="13px" disabled={disabled} blue>
              {row.lbname}
            </ReviewText>
          </Flex>
        </StyledTd>
        <OpenedCell row={row} disabled={disabled} red={red} />
        <EntryDateOrPatientCell
          row={row}
          isMedicalLog={isMedicalLog}
          disabled={disabled}
          red={red}
        />
        <RolesCells row={row} disabled={disabled} red={red} />
        {isMedicalLog ? (
          <MedicalStatusCells
            logStatus={row.logstatus}
            eventStatus={row.eventstatus}
            red={red}
          />
        ) : (
          <StatusCell review={row} singleCell={rowIndex === 0} red={red} />
        )}

        {!isPdfExport && (
          <ActionsCell
            row={row}
            openFiles={openFiles}
            openComments={openComments}
            red={red}
          />
        )}
        {isSelect && <SelectCell row={row} red={red} />}
      </StyledTr>
      {showNotesOnSummary && row.notes && (
        <StyledTr background="transparent">
          <StyledTd withoutImportant red={red}>
            Notes:
          </StyledTd>
          <StyledTd
            withoutImportant
            red={red}
            style={{ borderLeft: "none" }}
            colSpan={99}>
            {row.notes.map((note, idx) => (
              <div key={`notes--${idx}`}>
                {uppercase ? note?.toUpperCase() : note}
              </div>
            ))}
          </StyledTd>
        </StyledTr>
      )}
      {checkPerm("viewfiles") && (
        <FilesModal
          opened={filesOpened}
          close={closeFiles}
          toggleRemoved={toggleRemoved}
          showRemoved={showRemoved}
          removedNumber={removedNumber}
          _files={_files}
          readonly
        />
      )}
    </>
  );
};

export default SummaryItemRow;
